import type { EventEmitter } from '@stencil/core';
import { Component, Host, Prop, Event, Listen, Method, Element, AttachInternals, h } from '@stencil/core';
import { generateGuid, ToggleEventDetail } from '../../utils';
import { setFormValue, setValidityMissingValue } from '../../utils/formAssociated';

@Component({
  tag: 'anchor-toggle',
  styleUrl: 'toggle.scss',
  shadow: {
    delegatesFocus: true
  },
  formAssociated: true,
})
export class AnchorToggle {
  @Element() host: HTMLInputElement;
  
  @AttachInternals() internals: ElementInternals;

  formResetCallback() {
    this.internals.setValidity({});
    setFormValue(this.internals, null);
  }

  formStateRestoreCallback(state: string) {
    setFormValue(this.internals, state);
    setValidityMissingValue(this.internals, state, this.validityMissingValueText)
  }
  /**
   * The id of the input field
   */
  @Prop() inputId: string = `anchor-toggle-${generateGuid()}`;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop() name: string = this.inputId;

  /**
   * The value of the toggle component. It does not control if its checked or not. Use `checked` for that.
   */
  @Prop({ reflect: true, mutable: true }) value?: string = 'on'

  /**
   * If `true` the toggle is marked as required
   */
  @Prop() required: boolean = false;

  /**
   * If `true` the user cannot interact with the toggle
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * If `true` the toggle will be checked
   */
  @Prop({ reflect: true, mutable: true }) checked: boolean = false;

  /**
   * Text to be displayed if when value is missing an native form validation is used
   */
  @Prop() validityMissingValueText?: string;

  @Event() toggled: EventEmitter<ToggleEventDetail>;

  @Method()
  async toggleInput(event = undefined) {
    if (event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
    if (!this.disabled) {
      this.checked = !this.checked;
      setFormValue(this.internals, this.checked ? this.value: null)
      if (this.required)
        setValidityMissingValue(this.internals, this.checked ? this.value: null, this.validityMissingValueText);
      this.internals.ariaChecked = String(this.checked);
      this.toggled.emit({ checked: this.checked, value: this.value });
    }
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    switch (event.code) {
      case 'Enter':
      case 'Space':
        this.toggleInput(event);
        break;
      default:
        break;
    }
  }

  private readonly onClick = (event: MouseEvent) => {
    if (this.disabled) {
      return;
    }

    event.preventDefault();

    // TODO: Implement gesture logic
    this.toggleInput(event);
  };

  /**
   * Gets any content passed into the `label` slot,
   * not the <slot> definition.
   */
  private get toggleLabelId() {
    return `toggle-label-${this.inputId}`;
  }

  /**
   * Gets any content passed into the `label` slot,
   * not the <slot> definition.
   */
  private get labelSlot() {
    return this.host.querySelector('[slot="label"]');
  }

  render() {
    return (
      <Host
        role="switch"
        aria-checked={String(this.checked)}
        onClick={this.onClick}
        aria-labelledby={this.labelSlot ? this.toggleLabelId : null}
      >
        <anchor-label
          class={`
            anchor-c-toggle 
            ${this.disabled ? 'anchor-c-toggle--disabled' : ''} 
          `}
          for={this.inputId}
          tabindex="0"
        >
          <input
            id={this.inputId}
            name={this.name}
            type="checkbox"
            class={`anchor-c-toggle--input`}
            checked={this.checked}
            disabled={this.disabled}
            tabindex="-1"
            value={this.value}
          />
          <span class="text">
            <slot />
            <slot name="help-text" />
          </span>
        </anchor-label>
      </Host>
    );
  }
}
